// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getExpressCheckout } from "@hardwoods/App/Services/ExpressCheckoutService";
import { CheckoutStepKey } from "@hardwoods/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmit.helper";
import { isValidDate } from "@hardwoods/XDL/Utilities/helper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { ApiHandler, createHandlerChainRunner } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedPickUpDate";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetIsPreloadingData";

interface Parameter {
    onSuccess?: (nextUrl: string) => void;
    onError?: (error: string) => void;
}

type HandlerType = ApiHandler<Parameter, string, { isExpress: boolean; cartId?: string }>;

function getValidDate(blackOutDays: string[], currentDate: Date): Date {
    if (isValidDate(blackOutDays, currentDate)) {
        return currentDate;
    }
    return getValidDate(blackOutDays, addDays(currentDate, 1));
}

export const SetDefaultPreferredDate: HandlerType = props => {
    const state = props.getState();
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ cartId?: string }>(location.search);
    const settingsCollection = getSettingsCollection(state);
    props.cartId = parsedQuery.cartId;
    const cart = props.cartId ? getCartState(state, props.cartId).value : getCurrentCartState(state).value;
    const showPickUpDate =
        settingsCollection.accountSettings.enableWarehousePickup &&
        settingsCollection.cartSettings.enableRequestPickUpDate &&
        state.context.session.fulfillmentMethod === FulfillmentMethod.PickUp;

    const blackoutDays = (settingsCollection as any).customSettings.blackoutDays || [];
    const todayDate = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
    const tomorrowDate = addDays(todayDate, 1);
    if (showPickUpDate) {
        if (!cart?.requestedPickupDate || !isValidDate(blackoutDays, new Date(cart?.requestedPickupDate))) {
            props.dispatch(setRequestedPickUpDate({ requestedPickUpDate: getValidDate(blackoutDays, tomorrowDate) }));
        }
    } else if (
        state.context.session.fulfillmentMethod === FulfillmentMethod.Ship &&
        settingsCollection.cartSettings.canRequestDeliveryDate
    ) {
        if (!cart?.requestedDeliveryDate || !isValidDate(blackoutDays, new Date(cart?.requestedDeliveryDate))) {
            props.dispatch(
                setRequestedDeliveryDate({ requestedDeliveryDate: getValidDate(blackoutDays, tomorrowDate) }),
            );
        }
    }
};

export const RequestDataFromApi: HandlerType = props => {
    try {
        props.apiResult = "addresses";
    } catch (e) {
        props.parameter.onError?.(e && ((e.errorJson && e.errorJson.message) || e.errorMessage || e.message || e));
        return false;
    }
};

export const GetNextCheckoutUrl: HandlerType = props => {
    const state = props.getState();
    const checkoutShippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const checkoutReviewAndSubmitPageUrl = getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url;
    let nextUrl = checkoutShippingPageUrl || "";
    let isExpress = false;
    if (props.apiResult) {
        const nextStep = props.apiResult.toLocaleLowerCase();
        switch (nextStep) {
            case CheckoutStepKey.addresses:
                nextUrl = checkoutShippingPageUrl || "";
                break;
            case CheckoutStepKey.payment:
            case CheckoutStepKey.review:
                isExpress = true;
                nextUrl = checkoutReviewAndSubmitPageUrl || "";
                break;
        }
        props.isExpress = isExpress;
        nextUrl = `${nextUrl}?${props.cartId ? `cartId=${props.cartId}&` : ``}step=${nextStep}${
            isExpress ? `&express=true` : ``
        }`;
    } else {
        nextUrl = `${nextUrl}?${props.cartId ? `cartId=${props.cartId}&` : ``}`;
    }

    props.parameter.onSuccess?.(nextUrl);
};

export const DispatchCompleteExpressCheckout: HandlerType = props => {
    if (props.isExpress) {
        props.dispatch(setIsPreloadingData({ isPreloadingData: false }));
    }
    props.dispatch({
        type: "xCheckoutState/CompleteExpressCheckout" as any,
    });
};

export const chain = [SetDefaultPreferredDate, RequestDataFromApi, GetNextCheckoutUrl, DispatchCompleteExpressCheckout];

const xExpressCheckout = createHandlerChainRunner(chain, "xExpressCheckout");
export default xExpressCheckout;
